import React, { useMemo } from 'react';
import { Dropdown } from 'components/molecules/dropdown';
import { Card } from 'components/atoms/Card';
import { ArrowUpDown } from 'lucide-react';
import PropTypes from 'prop-types';
import { Box, Flex } from 'rebass/styled-components';
import { Select } from '@getro/rombo';
import { contactsSortOptions } from 'components/organisms/contactsFilters/defaultFilters';
import { useSelector } from 'react-redux';
import { activeNetworkSelector } from 'redux/selectors';
import { useHasRelationshipStrengthFeature } from 'hooks/useHasRelationshipStrengthFeature';
import { ToolbarButton } from './toolbarButton';

export const SortToolbar = ({ currentSort, toolbarOptionChange, label, selectedColumns, type, sortOptions }) => {
  const activeNetwork = useSelector(activeNetworkSelector);
  const isPublic = type === 'public';
  const tnHidden = activeNetwork?.tnHidden || !activeNetwork?.features?.includes('job_board');
  const hasRelationshipStrength = useHasRelationshipStrengthFeature({ network: activeNetwork });

  const availableSortOptions = useMemo(() => {
    if (sortOptions && sortOptions.length > 0) {
      return sortOptions;
    }

    // The reachability + connections sort options are gated on the
    // relationship_strength flag, mirroring the columns they sort.
    const baseSortOptions = hasRelationshipStrength
      ? contactsSortOptions
      : contactsSortOptions.filter((option) => option.field !== 'reachability' && option.field !== 'connections');

    if (isPublic && selectedColumns && selectedColumns.length > 0) {
      const visibleAccessors = new Set(selectedColumns.filter((col) => col.show !== false).map((col) => col.accessor));
      return baseSortOptions.filter((option) => option.field === 'relevance' || visibleAccessors.has(option.field));
    }

    return baseSortOptions;
  }, [isPublic, selectedColumns, sortOptions, hasRelationshipStrength]);

  const sortDirections = [
    { label: 'Ascending', value: 'asc' },
    { label: 'Descending', value: 'desc' },
  ];

  const selectedOption =
    availableSortOptions.find((option) => option.value === currentSort?.sortBy) || availableSortOptions[0];
  const selectedDirection =
    sortDirections.find((direction) => direction.value === currentSort?.sortDirection) || sortDirections[1];

  return (
    <Dropdown
      triggerProps={{
        variant: 'tertiary',
        size: 'sm',
      }}
      placement="bottom-start"
      trigger={({ isOpen }) => (
        <Flex sx={{ alignItems: 'center' }}>
          <ToolbarButton
            option={{
              icon: ArrowUpDown,
              label,
            }}
            active={isOpen}
          />
        </Flex>
      )}
    >
      <Card
        width="fit-content"
        minWidth="280px"
        p="16px"
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: '8px',
          alignItems: 'center',
          '.Select__menu-list': {
            maxHeight: '500px',
          },
        }}
      >
        <Flex sx={{ flexDirection: 'row', gap: '8px' }}>
          <Box sx={{ width: '260px', minWidth: '240px' }}>
            <Select
              size="small"
              options={availableSortOptions
                .filter((option) => !(option.field === 'signupDate' && tnHidden))
                .map((option) => ({
                  value: option.value,
                  label: option.label,
                }))}
              onChange={(value) => {
                toolbarOptionChange('sort', { value: value.value, direction: selectedDirection.value });
              }}
              value={selectedOption}
            />
          </Box>

          <Box sx={{ width: '144px', maxWidth: '144px' }}>
            <Select
              size="small"
              disabled={selectedOption.value === 'relevance'}
              options={sortDirections}
              onChange={(value) => {
                toolbarOptionChange('sort', { value: selectedOption.value, direction: value.value });
              }}
              value={selectedOption.value === 'relevance' ? { label: 'Descending', value: 'desc' } : selectedDirection}
            />
          </Box>
        </Flex>
      </Card>
    </Dropdown>
  );
};

SortToolbar.propTypes = {
  currentSort: PropTypes.shape({
    sortBy: PropTypes.string,
    sortDirection: PropTypes.oneOf(['asc', 'desc']),
  }),
  toolbarOptionChange: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
  selectedColumns: PropTypes.array,
  type: PropTypes.string,
  sortOptions: PropTypes.arrayOf(
    PropTypes.shape({
      field: PropTypes.string,
      label: PropTypes.string,
      value: PropTypes.string,
    }),
  ),
};

SortToolbar.defaultProps = {
  currentSort: null,
  selectedColumns: [],
  type: 'default',
  sortOptions: null,
};
